import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Grid } from "@material-ui/core";

import { endpoint } from "assets/api/endpoint";
import { apiHandler } from "assets/api";
import {
  diamondMasterData,
  commonButton,
  toastAction,
} from "assets/properties/properties";

import CircularProgresss from "components/CircularProgress/CircularProgresss";
import SubHeader from "components/UpdateUi/SubHeader/SubHeader";
import CustomeTextField from "components/UpdateUi/CustomeTextField";
import CustomSelectNew from "../../../components/UpdateUi/CustomeSelect";
import ToastModal from "views/components/ToastModal";
import { CARAT_COLOUR_LIST } from "assets/properties/properties";
import { CARAT_CLARITY_LIST } from "assets/properties/properties";

const DiamondMasterCreate = ({ classes }) => {
  const navigate = useNavigate();
  const { id } = useParams();

  const isEditMode = Boolean(id);

  const { authToken } = useSelector((state) => state.login.authData);

  const [caratColour, setCaratColour] = useState("");
  const [caratClarity, setCaratClarity] = useState("");
  const [minValue, setMinValue] = useState("");
  const [maxValue, setMaxValue] = useState("");

  const [caratColourError, setCaratColourError] = useState(false);
  const [caratClarityError, setCaratClarityError] = useState(false);
  const [minValueError, setMinValueError] = useState(false);
  const [maxValueError, setMaxValueError] = useState(false);
  const [amountRangeError, setAmountRangeError] = useState(false);

  const [callInProgress, setCallInProgress] = useState(false);
  const [toast, setToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastMessageType, setToastMessageType] = useState("");

  const toastUpdate = (type, message, showToast) => {
    setToastMessageType(type);
    setToastMessage(message);
    setToast(showToast);
  };
  const getDiamondMasterData = useCallback(async () => {
    if (!isEditMode) return;

    setCallInProgress(true);

    try {
      const result = await apiHandler({
        url: `${endpoint.DIAMOND_MASTER_FIND_ID}${id}`,
        authToken,
      });

      if (result?.data?.error_code) {
        toastUpdate(
          toastAction?.error,
          result?.data?.user_description ||
            "Unable to fetch Diamond Master data.",
          true
        );
        return;
      }

      // Supports either a direct object or a wrapped response.
      const responseData = result?.data?.data ?? result?.data;
      const selectedRecord = Array.isArray(responseData)
        ? responseData[0]
        : responseData;

      if (!selectedRecord || selectedRecord?.id == null) {
        toastUpdate(
          toastAction?.error,
          "Diamond Master record not found.",
          true
        );
        return;
      }

      setCaratColour(selectedRecord?.caretColor ?? "");
      setCaratClarity(selectedRecord?.caretGrade ?? "");
      setMinValue(
        selectedRecord?.minAmt != null ? String(selectedRecord.minAmt) : ""
      );
      setMaxValue(
        selectedRecord?.maxAmt != null ? String(selectedRecord.maxAmt) : ""
      );
    } catch (error) {
      toastUpdate(
        toastAction?.error,
        error?.message || "Unable to fetch Diamond Master data.",
        true
      );
    } finally {
      setCallInProgress(false);
    }
  }, [authToken, id, isEditMode]);

  useEffect(() => {
    getDiamondMasterData();
  }, [getDiamondMasterData]);
  const isDuplicateCaratCombination = async () => {
    const result = await apiHandler({
      url: endpoint.DIAMOND_MASTER_FIND_ALL,
      authToken,
    });

    if (result?.data?.error_code) {
      throw new Error(
        result?.data?.user_description ||
          "Unable to validate existing Diamond Master records."
      );
    }

    const responseData = result?.data?.data ?? result?.data;
    const records = Array.isArray(responseData) ? responseData : [];

    return records.some(
      (record) =>
        String(record?.caretColor ?? "")
          .trim()
          .toUpperCase() === String(caratColour).trim().toUpperCase() &&
        String(record?.caretGrade ?? "")
          .trim()
          .toUpperCase() === String(caratClarity).trim().toUpperCase()
    );
  };
  const isValidated = () => {
    const hasCaratColour = Boolean(caratColour);
    const hasCaratClarity = Boolean(caratClarity);
    const hasMinValue = minValue.trim() !== "";
    const hasMaxValue = maxValue.trim() !== "";

    setCaratColourError(!hasCaratColour);
    setCaratClarityError(!hasCaratClarity);
    setMinValueError(!hasMinValue);
    setMaxValueError(!hasMaxValue);
    setAmountRangeError(false);

    if (!hasCaratColour || !hasCaratClarity || !hasMinValue || !hasMaxValue) {
      return false;
    }

    const min = Number(minValue);
    const max = Number(maxValue);

    if (!Number.isFinite(min) || min < 0) {
      setMinValueError(true);
      return false;
    }

    if (!Number.isFinite(max) || max < 0) {
      setMaxValueError(true);
      return false;
    }

    if (min > max) {
      setAmountRangeError(true);
      return false;
    }

    return true;
  };

  const handleCreateSubmit = async () => {
    if (callInProgress || !isValidated()) {
      return;
    }

    setCallInProgress(true);

    try {
      // Prevent duplicate Carat Colour + Carat Clarity combinations on create.
      if (!isEditMode) {
        const isDuplicate = await isDuplicateCaratCombination();

        if (isDuplicate) {
          toastUpdate(
            toastAction?.error,
            "This Carat Colour and Carat Clarity combination already exists.",
            true
          );
          return;
        }
      }

      const payload = {
        caretColor: caratColour,
        caretGrade: caratClarity,
        minAmt: String(minValue).trim(),
        maxAmt: String(maxValue).trim(),
      };

      const res = await apiHandler({
        url: isEditMode
          ? `${endpoint.DIAMOND_MASTER_UPDATE}${id}`
          : endpoint.DIAMOND_MASTER_CREATE,
        method: "POST",
        authToken,
        data: payload,
      });

      if (res?.data?.error_code) {
        toastUpdate(
          toastAction?.error,
          res?.data?.user_description ||
            `Unable to ${isEditMode ? "update" : "create"} Diamond Master.`,
          true
        );
        return;
      }

      toastUpdate(
        toastAction?.success,
        res?.data?.user_description ||
          `Diamond Master ${isEditMode ? "updated" : "created"} successfully.`,
        true
      );

      navigate(diamondMasterData?.mainUrl);
    } catch (error) {
      toastUpdate(
        toastAction?.error,
        error?.message ||
          `Unable to ${isEditMode ? "update" : "create"} Diamond Master.`,
        true
      );
    } finally {
      setCallInProgress(false);
    }
  };

  const handleCancel = () => {
    navigate(diamondMasterData?.mainUrl);
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <SubHeader
          label={isEditMode ? "Edit Diamond Master" : "Diamond Master Create"}
          button1={isEditMode ? "Update" : commonButton?.button1}
          button2={commonButton?.button2}
          button1click={handleCreateSubmit}
          disabled1={callInProgress}
          button2click={handleCancel}
        />
      </Grid>

      <Grid item xs={12} className={classes.inputContainer}>
        <Grid container className={classes.inputFieldContainer}>
          <Grid item xs={12} sm={6} md={4} className={classes.fieldContaienr}>
            <CustomSelectNew
              label="Carat Colour*"
              id="carat_colour"
              value={caratColour}
              setState={(value) => {
                setCaratColour(value);
                setCaratColourError(false);
              }}
              data={CARAT_COLOUR_LIST}
              error={caratColourError}
              setError={setCaratColourError}
              disabled={isEditMode}
              type="value"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} className={classes.fieldContaienr}>
            <CustomSelectNew
              label="Carat Clarity*"
              id="carat_clarity"
              value={caratClarity}
              setState={(value) => {
                setCaratClarity(value);
                setCaratClarityError(false);
              }}
              data={CARAT_CLARITY_LIST}
              error={caratClarityError}
              setError={setCaratClarityError}
              disabled={isEditMode}
              type="value"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} className={classes.fieldContaienr}>
            <CustomeTextField
              label="Min Value(per caret)"
              required
              id="min_value"
              value={minValue}
              error={minValueError || amountRangeError}
              setState={(value) => {
                setMinValue(value);
                setMinValueError(false);
                setAmountRangeError(false);
              }}
              type="number"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} className={classes.fieldContaienr}>
            <CustomeTextField
              label="Max Value(per caret)"
              required
              id="max_value"
              value={maxValue}
              error={maxValueError || amountRangeError}
              setState={(value) => {
                setMaxValue(value);
                setMaxValueError(false);
                setAmountRangeError(false);
              }}
              type="number"
            />
          </Grid>
          {amountRangeError && (
            <Grid item xs={12}>
              <span style={{ color: "red" }}>
                Min Value cannot be greater than Max Value.
              </span>
            </Grid>
          )}
        </Grid>
      </Grid>
      {callInProgress && <CircularProgresss callInProgress={callInProgress} />}
      {toast && (
        <ToastModal
          toastMessage={toastMessage}
          toastMessageType={toastMessageType}
          toastUpdate={toastUpdate}
        />
      )}
    </Grid>
  );
};

DiamondMasterCreate.propTypes = {
  classes: PropTypes.object,
};

export default DiamondMasterCreate;